import { set } from "nodash"

const oneTimeWarningCache = {} as Record<string, boolean>

/**
 * to help avoid forgetting to unguard features, pass a featureId to the isDevelopmentEnvironment call
 */
const resultWithWarnings = ({
    result,
    featureId,
    warnOnlyOnce,
    environmentName = "development",
}: {
    result: boolean
    featureId: string
    warnOnlyOnce: boolean
    environmentName?: string
}) => {
    const hasBeenWarned = oneTimeWarningCache[featureId] ?? false
    if (featureId && (!warnOnlyOnce || !hasBeenWarned)) {
        console.log(`WARNING: this feature is only visible in ${environmentName} environments`, {
            featureId,
        })
        if (warnOnlyOnce) {
            set(oneTimeWarningCache, featureId, true)
        }
    }

    return result
}

// this will only be true in npm run dev scenarios -- localhost generally speaking.
export const isLocalDevelopmentEnvironment = (featureId = "", warnOnlyOnce = true) =>
    resultWithWarnings({
        result: process.env.NODE_ENV === "development",
        featureId,
        warnOnlyOnce,
        environmentName: "local-development",
    })

/**
 * if console is setup, print a warning which shows some context details and then throw the message
 *
 * @param message
 * @param context
 */
export const throwAfterConsole = (
    message: string,
    context: Record<string, unknown>,
    reportAs: "error" | "warn" = "error"
): never => {
    if (
        reportAs === "warn" &&
        typeof console !== "undefined" &&
        typeof console.warn === "function"
    ) {
        console.warn({ message, context })
    }

    if (
        reportAs === "error" &&
        typeof console !== "undefined" &&
        typeof console.error === "function"
    ) {
        console.error({ message, context })
    }

    throw new Error(message)
}

/**
 * use this to make ts see the fact that we will not continue code execution. throwAfterConsole does not work so well for that.
 */
export class ErrorWithConsoleWarning {
    constructor(message: string, context: Record<string, unknown> = {}) {
        throwAfterConsole(message, context, "warn")
    }
}

/**
 * use this to make ts see the fact that we will not continue code execution. throwAfterConsole does not work so well for that.
 */
export class ErrorWithConsoleTrace {
    constructor(message: string, context: Record<string, unknown> = {}) {
        throwAfterConsole(message, context, "error")
    }
}

export const isProduction = () => Boolean(process.env.NODE_ENV === "production")

type DebugWindow = typeof window & {
    traceAction?: string
    traceActions?: boolean
    logSocketDisconnections?: boolean
    printActions?: () => void
    useOriginalFireQuery?: boolean
}
export const getDebugWindow = () => window as DebugWindow
